import React, { useState, useEffect } from "react";
import { api } from "../api/api";
import ManageContactsModal from "./ManageContactsModal";
import ManageCategoriesModal from "./ManageCategoriesModal";

const defaultAccounts = [
  { key: "bank1", label: "Bank of Queensland (BOQ)" },
  { key: "bank2", label: "Commonwealth Bank (CBA)" },
  { key: "cash", label: "Cash" },
];

const initialForm = {
  date: "",
  clientOrSupplier: "",
  description: "",
  category: "",
  bank1: "",
  bank2: "",
  cash: "",
};

function formatMoney(val) {
  if (val === "" || isNaN(val)) return "";
  const number = Number(val);
  const sign = number >= 0 ? "+" : "–";
  return (
    "$\u202f" +
    sign +
    Math.abs(number).toLocaleString("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

export default function TransactionForm({
  onSuccess,
  accounts = defaultAccounts,
  editId,
  editData,
  onEditSave,
  onEditCancel,
}) {
  const [form, setForm] = useState(initialForm);

  // Stato per categories & contacts
  const [categories, setCategories] = useState([]);
  const [contacts, setContacts] = useState([]);

  // Stato per apertura modali gestionali
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [openCategoriesModal, setOpenCategoriesModal] = useState(false);

  // Carica dati su mount o dopo modifica da modali
  const fetchContacts = async () => {
    const data = await api.get("/contacts");
    setContacts(data);
  };
  const fetchCategories = async () => {
    const data = await api.get("/categories");
    setCategories(data);
  };

  useEffect(() => {
    fetchCategories();
    fetchContacts();
  }, []);

  // Carica in form i dati per l’edit
  useEffect(() => {
    if (editId && editData) {
      setForm({
        date: editData.date || "",
        clientOrSupplier:
          typeof editData.clientOrSupplier === "object"
            ? editData.clientOrSupplier.id
            : editData.clientOrSupplier || "",
        description: editData.description || "",
        category:
          typeof editData.category === "object"
            ? editData.category.id
            : editData.category || "",
        bank1: editData.bank1 || "",
        bank2: editData.bank2 || "",
        cash: editData.cash || "",
      });
    } else {
      setForm(initialForm);
    }
  }, [editId, editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (
      ["bank1", "bank2", "cash"].includes(name) &&
      value !== "" &&
      !/^[-+]?(\d+)?(\.\d{0,2})?$/.test(value)
    ) {
      return;
    }
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const clientObj = contacts.find(c => c.id === form.clientOrSupplier);
    const categoryObj = categories.find(cat => cat.id === form.category);

    const tx = {
      ...form,
      bank1: form.bank1 === "" ? 0 : Number(form.bank1),
      bank2: form.bank2 === "" ? 0 : Number(form.bank2),
      cash: form.cash === "" ? 0 : Number(form.cash),
      clientOrSupplier: clientObj ? clientObj : form.clientOrSupplier,
      category: categoryObj ? categoryObj : form.category,
    };

    if (editId && onEditSave) {
      await onEditSave(tx);
      setForm(initialForm);
    } else {
      await api.post("/transactions", tx);
      setForm(initialForm);
      onSuccess && onSuccess();
    }
  };

  return (
    <>
      {/* Modali gestionali */}
      <ManageContactsModal
        isOpen={openContactsModal}
        onClose={() => setOpenContactsModal(false)}
        onChanged={fetchContacts}
      />
      <ManageCategoriesModal
        isOpen={openCategoriesModal}
        onClose={() => setOpenCategoriesModal(false)}
        onChanged={fetchCategories}
      />

      <form className="transaction-form transaction-form-2col" onSubmit={handleSubmit}>
        <div className="form-col">
          <label>
            Date:
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              required
              style={{ fontFamily: "inherit" }}
            />
          </label>
          <label>
            {accounts[0].label}:
            <input
              type="text"
              name={accounts[0].key}
              value={form[accounts[0].key]}
              onChange={handleChange}
              placeholder="+0.00"
              inputMode="decimal"
              autoComplete="off"
              pattern="^[-+]?\d*(\.\d{0,2})?$"
              style={{ fontFamily: "monospace" }}
            />
            <span className="aud-currency-label">{formatMoney(form[accounts[0].key])}</span>
          </label>
          <label>
            {accounts[1].label}:
            <input
              type="text"
              name={accounts[1].key}
              value={form[accounts[1].key]}
              onChange={handleChange}
              placeholder="+0.00"
              inputMode="decimal"
              autoComplete="off"
              pattern="^[-+]?\d*(\.\d{0,2})?$"
              style={{ fontFamily: "monospace" }}
            />
            <span className="aud-currency-label">{formatMoney(form[accounts[1].key])}</span>
          </label>
          <label>
            {accounts[2].label}:
            <input
              type="text"
              name={accounts[2].key}
              value={form[accounts[2].key]}
              onChange={handleChange}
              placeholder="+0.00"
              inputMode="decimal"
              autoComplete="off"
              pattern="^[-+]?\d*(\.\d{0,2})?$"
              style={{ fontFamily: "monospace" }}
            />
            <span className="aud-currency-label">{formatMoney(form[accounts[2].key])}</span>
          </label>
        </div>
        <div className="form-col">
          <label>
            <div style={{ display: "flex", alignItems: "center" }}>
              Client/Supplier:
              <button
                type="button"
                className="modalqpsc-btn settings"
                style={{ marginLeft: 8, marginBottom: 0, fontSize: "0.98em" }}
                title="Manage Clients/Suppliers"
                onClick={() => setOpenContactsModal(true)}
              >⚙️</button>
            </div>
            <select
              name="clientOrSupplier"
              value={form.clientOrSupplier}
              onChange={handleChange}
              required
            >
              <option value="">Select...</option>
              {contacts.map((c, i) => (
                <option key={c.id || i} value={c.id}>
                  {c.isCompany ? c.companyName : `${c.firstName} ${c.lastName}`}
                </option>
              ))}
            </select>
          </label>
          <label>
            Description:
            <input
              type="text"
              name="description"
              value={form.description}
              onChange={handleChange}
            />
          </label>
          <label>
            <div style={{ display: "flex", alignItems: "center" }}>
              Category:
              <button
                type="button"
                className="modalqpsc-btn settings"
                style={{ marginLeft: 8, marginBottom: 0, fontSize: "0.98em" }}
                title="Manage Categories"
                onClick={() => setOpenCategoriesModal(true)}
              >⚙️</button>
            </div>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              required
            >
              <option value="">Select...</option>
              {categories.map((c, i) => (
                <option key={c.id || i} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
        </div>
        <button
          type="submit"
          className="transaction-form-btn"
        >
          {editId ? "Save Changes" : "Add Transaction"}
        </button>
        {editId && (
          <button
            type="button"
            className="transaction-form-btn"
            style={{ background: "#333", color: "#fff", borderColor: "#c9b076" }}
            onClick={onEditCancel}
          >
            Cancel
          </button>
        )}
      </form>
    </>
  );
}           